import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import type {
  PlaybookId,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import {
  isUnmeasuredProposalDetail,
  type CriteriaProposalDetail,
  type OutcomeProposalDetail,
  type SectionProposalDetail,
  type UnmeasuredProposalDetail,
} from "@repo/app-types/entities/playbookProposalDetail";
import { useCallback, useMemo, useRef, useState } from "react";
import { useResolveProposalMutation } from "../api/entities/copilotProposal.js";
import {
  useLazyFetchSampleToolResultsQuery,
  useLazyFetchTestResultsQuery,
  type SampleToolResultApiEntry,
} from "../api/entities/playbook.js";

/**
 * One sample-tool result entry, keyed by the saved row's id: the
 * `proposal_id` or `test_result_id` the agent tool returned. `proposal_id` is
 * set only for a proposal, whose Apply / Discard resolve it; a test result
 * renders as grades alone. Discriminated on `node_type`: criterion entries
 * carry outcome-correlation aggregates, section entries carry applicability
 * aggregates.
 */
export type SampleToolResultEntry = SampleToolResultApiEntry;

type SampleToolResultEntryBase = {
  id: CopilotProposalId | ConfigurationTestResultId;
  proposal_id: CopilotProposalId | null;
  playbook_node_id: PlaybookNodeId;
};

export type SampleToolResultCriterionEntry = SampleToolResultEntryBase &
  CriteriaProposalDetail;

export type SampleToolResultSectionEntry = SampleToolResultEntryBase &
  SectionProposalDetail;

export type SampleToolResultOutcomeEntry = SampleToolResultEntryBase &
  OutcomeProposalDetail;

export type SampleToolResultRemovalEntry = SampleToolResultEntryBase &
  UnmeasuredProposalDetail;

/**
 * Where the editor selects an entry: the node it is about, or the ghost row a
 * create proposal renders as.
 */
export type TestResultSelection =
  | "criteria"
  | "section"
  | "outcome"
  | "ghost-criterion"
  | "ghost-section";

export function readTestResultSelection(
  entry: SampleToolResultEntry,
): TestResultSelection {
  if (entry.node_type === "outcome") {
    return "outcome";
  }
  const kind = "update" in entry ? entry.update?.kind : undefined;
  if (kind === "create_criterion") {
    return "ghost-criterion";
  }
  if (kind === "create_section") {
    return "ghost-section";
  }
  return entry.node_type;
}

/**
 * A removal carries no draft and no measurements, so it renders and applies
 * differently from an edit — which is the only reason it needs telling apart.
 */
export function isRemovalSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultRemovalEntry {
  return isUnmeasuredProposalDetail(entry);
}

export function isSectionSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultSectionEntry {
  return entry.node_type === "section" && !isUnmeasuredProposalDetail(entry);
}

export function isOutcomeSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultOutcomeEntry {
  return entry.node_type === "outcome";
}

export function isCriterionSampleToolResultEntry(
  entry: SampleToolResultEntry,
): entry is SampleToolResultCriterionEntry {
  return entry.node_type === "criteria" && !isUnmeasuredProposalDetail(entry);
}

/**
 * The proposal shown on a node, whatever kind it is — the editor shows the one
 * most recently opened, even when the node has other pending ones. Proposed nodes
 * that don't exist in the tree yet (ghosts) are addressed the same way: their
 * generated id is their `playbook_node_id`.
 */
export function findProposalForNode(
  results: UseSampleToolResultsResult["results"] | undefined,
  nodeId: string,
): SampleToolResultEntry | undefined {
  return Object.values(results ?? {}).find(
    (entry) => entry.playbook_node_id === nodeId,
  );
}

/**
 * The playbook's outcome proposal. Found by node type rather than id: a
 * playbook has at most one outcome, and its row is keyed by a synthetic uuid
 * rather than a tree-node id.
 */
export function findOutcomeProposalEntry(
  results: UseSampleToolResultsResult["results"] | undefined,
): SampleToolResultEntry | undefined {
  return Object.values(results ?? {}).find(isOutcomeSampleToolResultEntry);
}

/**
 * Proposals for nodes that don't exist yet, scoped to the parent section they
 * would be inserted under (`null` for top level). The editor renders each as a
 * ghost row in the tree.
 */
export function getNewNodeProposalsForParent(
  results: UseSampleToolResultsResult["results"] | undefined,
  nodeType: "criteria" | "section",
  parentSectionId: string | null,
): SampleToolResultEntry[] {
  const createKind =
    nodeType === "criteria" ? "create_criterion" : "create_section";
  return Object.values(results ?? {}).filter((entry) => {
    const update = "update" in entry ? entry.update : undefined;
    return (
      update !== undefined &&
      update.kind === createKind &&
      "parent_section_id" in update &&
      (update.parent_section_id ?? null) === parentSectionId
    );
  });
}

/**
 * Typed views over {@link findProposalForNode}. A node's proposal can be an
 * edit or a removal, and the two drive different UI, so each caller asks for
 * the shape it renders rather than narrowing at the call site.
 */
export function findCriterionProposalForNode(
  results: UseSampleToolResultsResult["results"] | undefined,
  nodeId: string,
): SampleToolResultCriterionEntry | undefined {
  const entry = findProposalForNode(results, nodeId);
  return entry && isCriterionSampleToolResultEntry(entry) ? entry : undefined;
}

export function findSectionProposalForNode(
  results: UseSampleToolResultsResult["results"] | undefined,
  nodeId: string,
): SampleToolResultSectionEntry | undefined {
  const entry = findProposalForNode(results, nodeId);
  return entry && isSectionSampleToolResultEntry(entry) ? entry : undefined;
}

export function findRemovalProposalForNode(
  results: UseSampleToolResultsResult["results"] | undefined,
  nodeId: string,
): SampleToolResultRemovalEntry | undefined {
  const entry = findProposalForNode(results, nodeId);
  return entry && isRemovalSampleToolResultEntry(entry) ? entry : undefined;
}

export function findOutcomeProposal(
  results: UseSampleToolResultsResult["results"] | undefined,
): SampleToolResultOutcomeEntry | undefined {
  const entry = findOutcomeProposalEntry(results);
  return entry && isOutcomeSampleToolResultEntry(entry) ? entry : undefined;
}

export function getProposedCriteriaForSection(
  results: UseSampleToolResultsResult["results"] | undefined,
  sectionId: string,
): SampleToolResultCriterionEntry[] {
  return getNewNodeProposalsForParent(results, "criteria", sectionId).filter(
    isCriterionSampleToolResultEntry,
  );
}

export function getProposedSectionsForParent(
  results: UseSampleToolResultsResult["results"] | undefined,
  parentSectionId: string | null,
): SampleToolResultSectionEntry[] {
  return getNewNodeProposalsForParent(
    results,
    "section",
    parentSectionId,
  ).filter(isSectionSampleToolResultEntry);
}

export interface UseSampleToolResultsResult {
  /**
   * Map of row id (`proposal_id` or `test_result_id`) → entry. Populated as
   * the assistant runs sample tools during its turns.
   */
  results: Readonly<Record<string, SampleToolResultEntry>>;

  /**
   * Fetch a batch of `proposal_id`s and merge them into `results`. The editor
   * shows the proposal being viewed, so the merge drops any other entry on the
   * same node. The fetch always hits the server (no cached value) so a
   * resolved id resolves to nothing rather than a stale payload.
   */
  addIdsToFetch: (ids: readonly CopilotProposalId[]) => void;

  /**
   * Fetch a batch of `test_result_id`s (the Test button's saved test results)
   * and merge them into `results` the same way.
   */
  addTestResultIdsToFetch: (ids: readonly ConfigurationTestResultId[]) => void;

  /**
   * Resolve the node's proposal (`applied` or `discarded`) and drop its
   * entry from the local results map. Fired when the user accepts or
   * discards a suggestion. A test result has no proposal to resolve; its
   * entry is just dropped.
   */
  resolveResultForNode: (
    playbookNodeId: PlaybookNodeId,
    status: "applied" | "discarded",
  ) => void;

  /**
   * Drop every entry. Use after a lifecycle mutation (publish, draft-discard,
   * rollback) — the server discards the playbook's pending proposals on
   * those transitions, so the client cache would otherwise display stale
   * ghosts and suggestions.
   */
  clearResults: () => void;
}

/**
 * High-level playbook-editor variable storing sample-tool results the
 * AI assistant has produced this session. Populated only by detecting
 * agent turn completion in `ConfigurationAssistantChat` and pushing the
 * captured `proposal_id`s here via `addIdsToFetch` — the map starts empty on
 * every mount; saved DB rows are not rehydrated.
 *
 * Storage shape: `Record<id, entry>`, where `entry` is
 * `{ id, proposal_id, playbook_node_id } & PlaybookProposalDetail`.
 */
export function useSampleToolResults(
  playbookId: PlaybookId | null,
): UseSampleToolResultsResult {
  const [results, setResults] = useState<Record<string, SampleToolResultEntry>>(
    {},
  );

  // Reset state when the playbook changes — the results are scoped to
  // the playbook being edited.
  const prevPlaybookIdRef = useRef(playbookId);
  if (prevPlaybookIdRef.current !== playbookId) {
    prevPlaybookIdRef.current = playbookId;
    setResults({});
  }

  const [triggerFetch] = useLazyFetchSampleToolResultsQuery();
  const [triggerTestResultsFetch] = useLazyFetchTestResultsQuery();

  const mergeFetched = useCallback(
    (fetched: readonly SampleToolResultEntry[]) => {
      if (fetched.length === 0) {
        return;
      }
      setResults((prev) => {
        const next = { ...prev };
        // A node shows the proposal being viewed; drop any other entry on it.
        const fetchedNodeIds = new Set(
          fetched.map((entry) => entry.playbook_node_id),
        );
        for (const [id, entry] of Object.entries(prev)) {
          if (fetchedNodeIds.has(entry.playbook_node_id)) {
            delete next[id];
          }
        }
        for (const entry of fetched) {
          next[entry.id] = entry;
        }
        return next;
      });
    },
    [],
  );

  const addIdsToFetch = useCallback(
    (ids: readonly CopilotProposalId[]) => {
      if (!playbookId || ids.length === 0) {
        return;
      }
      // The same id can appear twice within one turn; collapse before fetching.
      const uniqueIds = [...new Set(ids)];
      void (async () => {
        try {
          // `preferCacheValue: false` forces a fresh request even for ids
          // already loaded this session — a resolved proposal id resolves to
          // nothing, so a cached payload would be stale.
          mergeFetched(
            await triggerFetch({ playbookId, ids: uniqueIds }, false).unwrap(),
          );
        } catch {
          // Network / permission failure — leave existing results untouched.
        }
      })();
    },
    [playbookId, triggerFetch, mergeFetched],
  );

  const addTestResultIdsToFetch = useCallback(
    (ids: readonly ConfigurationTestResultId[]) => {
      if (!playbookId || ids.length === 0) {
        return;
      }
      const uniqueIds = [...new Set(ids)];
      void (async () => {
        try {
          mergeFetched(
            await triggerTestResultsFetch({
              playbookId,
              ids: uniqueIds,
            }).unwrap(),
          );
        } catch {
          // Network / permission failure — leave existing results untouched.
        }
      })();
    },
    [playbookId, triggerTestResultsFetch, mergeFetched],
  );

  const [resolveProposal] = useResolveProposalMutation();

  const resolveResultForNode = useCallback(
    (playbookNodeId: PlaybookNodeId, status: "applied" | "discarded") => {
      const proposalId = Object.values(results).find(
        (entry) => entry.playbook_node_id === playbookNodeId,
      )?.proposal_id;
      setResults((prev) => {
        let didChange = false;
        const next: Record<string, SampleToolResultEntry> = {};
        for (const [id, entry] of Object.entries(prev)) {
          if (entry.playbook_node_id === playbookNodeId) {
            didChange = true;
            continue;
          }
          next[id] = entry;
        }
        return didChange ? next : prev;
      });
      // Record the decision server-side (a status transition, addressed by
      // proposal id so another pending proposal on the node is never resolved
      // by mistake).
      // Fire-and-forget; the local state already updated. A playbook that was
      // never saved has no proposals to resolve.
      if (proposalId) {
        void resolveProposal({ proposalId, status });
      }
    },
    [results, resolveProposal],
  );

  const clearResults = useCallback(() => {
    setResults({});
  }, []);

  return useMemo(
    () => ({
      results,
      addIdsToFetch,
      addTestResultIdsToFetch,
      resolveResultForNode,
      clearResults,
    }),
    [
      results,
      addIdsToFetch,
      addTestResultIdsToFetch,
      resolveResultForNode,
      clearResults,
    ],
  );
}
